/* istanbul ignore file */

import axios, { CancelTokenSource } from "axios";
import { objectToCamel, objectToSnake } from "ts-case-convert";
import subdomain from "lib/subdomain";
import TokenService from "./token.service";
import _ from "lodash";
import { PickupContact } from "types/pickupContact";
import * as Sentry from "@sentry/browser";
import { isProduction } from "../utils/helpers";

export const HOSTNAME = process.env.REACT_APP_API_BASE_URL || "";

/** Base URL for API requests (no interceptors). Used by AuthResolver for bootstrap auth check. */
export const API_BASE_URL = isProduction() ? "/api/v1" : `${HOSTNAME}api/v1`;

// In production, use relative URL to leverage Vercel proxy (avoids CORS/firewall issues)
// In development, use full URL to connect directly to backend
export const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
});

const requestCancelSourcesByKey: { [cancelKey: string]: CancelTokenSource } = {};

export const cancelRequest = (cancelKey: string) => {
  const cancelSource = requestCancelSourcesByKey[cancelKey];
  if (cancelSource) {
    cancelSource.cancel();
    delete requestCancelSourcesByKey[cancelKey];
  }
};

export const emptyStringsToNull = (data: PickupContact[]) => {
  return data.map((contact: PickupContact) => {
    return _.mapValues(contact, (value) => (value === "" ? null : value));
  });
};

api.interceptors.request.use(
  (config: any) => {
    // Cancel previous requests with the same key
    if (config.cancelKey) {
      cancelRequest(config.cancelKey);
      const newCancelSource = axios.CancelToken.source();
      requestCancelSourcesByKey[config.cancelKey] = newCancelSource;
      config.cancelToken = newCancelSource.token;
    }

    // Transform request data to snake_case
    if (config.data) {
      config.data = objectToSnake(config.data);

      if (config.data.location) {
        if (config.data.location.address_2) {
          config.data.location["address2"] = config.data.location.address_2;
          delete config.data.location["address_2"];
        }
        if (config.data.location.location_contacts) {
          config.data.location.location_contacts = emptyStringsToNull(
            config.data.location.location_contacts
          );
        }
      }
    }

    const url = config.url || "";
    const skipAuthHeader = [
      "/login",
      "/verify_mfa",
      "/resend_mfa",
      "/magic_link",
      "/validate_magic_link_token",
      "/reset_password",
      "/set_password",
    ].some((path) => url === path || url.endsWith(path) || url.includes(`${path}?`));

    // Set Authorization header with a short-lived token
    const token = TokenService.get();
    if (token && !skipAuthHeader) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Simplify headers by removing unnecessary or redundant ones
    if (config.headers) {
      if (typeof (config.headers as any).set === "function") {
        (config.headers as any).set("Accept", "application/json");
      } else {
        config.headers.Accept = "application/json";
      }

      // Axios v1 often omits `headers.common`; avoid property access on undefined
      const common = (config.headers as any).common;
      if (common && typeof common === "object") {
        delete common["Pragma"];
        delete common["Cache-Control"];
      }

      const organizationSubdomain = subdomain();
      if (organizationSubdomain) {
        if (typeof (config.headers as any).set === "function") {
          (config.headers as any).set("organization-subdomain", organizationSubdomain);
        } else {
          if (!common) {
            (config.headers as any).common = {};
          }
          (config.headers as any).common["organization-subdomain"] =
            organizationSubdomain;
        }
      }
    }

    return config;
  },
  (error: Error) => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  function (response) {
    const raw = response.data;
    const data =
      raw === null || raw === undefined
        ? raw
        : typeof raw === "object"
          ? objectToCamel(raw)
          : raw;
    return { ...response, data };
  },
  function (error) {
    console.log("error", error);
    Sentry.captureException(error);
    if (error?.code === "ERR_NETWORK") {
      if (!window.location.href.includes("no-backend-connection"))
        window.location.href = "/no-backend-connection";
      return Promise.reject(error);
    }

    if (
      error.response?.status === 401 &&
      !window.location.href.includes("login") &&
      !["/verify_mfa", "/resend_mfa", "/magic_link", "/reset_password", "/set_password"].some(
        (path) => error.config?.url?.includes(path)
      )
    ) {
      const errBody = error.response?.data as { error?: string } | undefined;
      const suffix =
        errBody?.error === "organization_mismatch" ? "?org_mismatch=1" : "";
      window.location.href = `/login${suffix}`;
    }

    return Promise.reject(error);
  }
);
